/**
 * /faq — collapsible Q&As across product, methodology, and billing.
 */

import React from 'react';
import PageHeader from '../../components/chrome/PageHeader';
import { usePageMeta } from '../../hooks/usePageMeta';

interface QA { q: string; a: React.ReactNode; }

const SECTIONS: { title: string; items: QA[] }[] = [
  {
    title: 'Product',
    items: [
      { q: 'What is GEX?', a: 'Gamma Exposure — the dollar amount of underlying that market makers must buy or sell as price moves to keep their book delta-neutral. High positive GEX dampens moves; negative GEX amplifies them.' },
      { q: 'What is VEX?', a: 'Vanna Exposure — sensitivity of dealer delta to changes in implied volatility. When IV drops, positive vanna forces dealers to buy underlying, putting upward pressure on price.' },
      { q: 'How do I read the heatmap?', a: 'Rows are strikes (highest at top). Columns are expirations (sorted left-to-right). Cells sit on a muted base that tints gold as positive (long-gamma) exposure grows and red as negative (short-gamma) exposure grows. The single strongest call wall (gold) and put wall (red) glow at full intensity with a ring. The dashed red line is the GEX flip; the white line is current spot.' },
      { q: 'What is a King? Gatekeeper? Hedge?', a: 'King: the strike with the largest absolute exposure — the strongest dealer wall. Gatekeeper: secondary high-exposure strike that forms support/resistance. Hedge: minor exposure typically used for risk offset.' },
      { q: 'What is a flip point?', a: 'The strike where aggregate GEX crosses from positive to negative. Above the flip, dealers dampen moves. Below, dealers amplify them — vol expansion regime.' },
      { q: 'What is an air pocket?', a: 'A contiguous strike range with negligible exposure. Once price enters one, dealer hedging can\'t slow it — moves accelerate. Cascade zones.' },
    ],
  },
  {
    title: 'Data & Methodology',
    items: [
      { q: 'How fresh is the data?', a: 'Options chains are pulled every 30 seconds during market hours. The recomputed heatmap is broadcast over WebSocket within ~200 ms of fetch. After hours we keep the last regular-session frame.' },
      { q: 'Which providers do you support?', a: 'Today: Tradier (default), Yahoo Finance (free, optional), and a mock provider for demos. Polygon.io support is on the roadmap.' },
      { q: 'How are Greeks computed?', a: 'Black-Scholes on the implied volatility returned by your data provider. We compute gamma, delta, vanna, and vega ourselves; theta and rho come from the provider when available.' },
      { q: 'What\'s the dealer-flow assumption?', a: 'We assume dealers are short calls and long puts net of customer flow — the standard Heatseeker / SpotGamma convention. The model holds well intraday but can be inverted around major events.' },
    ],
  },
  {
    title: 'Plans & Billing',
    items: [
      { q: 'Do you offer an API?', a: 'Yes — Pro tier exposes the same WebSocket and REST endpoints the web app uses. Email api@gammavision.app for the OpenAPI schema once your account is provisioned.' },
      { q: 'Do you offer alerts?', a: 'On the Pro tier. Slack and Discord webhooks land first; SMS / email coming next. Trigger conditions are exposed via the Scanner page.' },
      { q: 'Is this trading advice?', a: 'No. GammaVision is an analytical tool. Nothing on this site is a recommendation to buy, sell, or hold any security. Verify all numbers against your broker before acting on them.' },
      { q: 'How do I cancel?', a: 'Account settings → Billing → Cancel. Or reply to any GammaVision email and we\'ll take care of it. No questions asked.' },
    ],
  },
];

const FAQ: React.FC = () => {
  usePageMeta(
    'FAQ — GammaVision',
    'How GammaVision computes GEX, VEX, King strikes, and flip points. Plus billing & cancellation answers.',
  );

  return (
    <>
      <PageHeader title="FAQ" subtitle="What GammaVision is, what it isn't, and how the numbers come together." />

      <div className="gv-options-body">
        {SECTIONS.map(section => (
          <section className="gv-card" key={section.title}>
            <div className="gv-panel-title">{section.title}</div>
            <div className="gv-faq">
              {section.items.map((qa, i) => (
                <details key={i}>
                  <summary>{qa.q}</summary>
                  <div>{qa.a}</div>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
};

export default FAQ;
